import { useQuery, useMutation } from 'react-query'

import useAccountQueries, { useDeleteUserMutation } from '../api/accountQueries'
import useGetAccountData from '../ui/common/useGetAccountData'

import { useInitializeFeatureFlags } from 'app/ui/useInitializeFeatureFlags'
import { createSelector } from '@reduxjs/toolkit'
import { RootState } from 'app/state/store'
import { useAppSelector } from 'app/state'

const launchDarklyContextFromAccount = createSelector(
  (state: RootState) => state.account?.data,
  (accountData) => {
    if (!accountData?.id) return undefined

    return {
      kind: 'user',
      key: accountData.id,
      name: accountData.name,
      plan: accountData.plan?.name
    }
  }
)

export function useUserAccountBasedFeatureFlags() {
  // ensure account data is loaded
  useGetAccountData()
  const ldContext = useAppSelector(launchDarklyContextFromAccount)
  useInitializeFeatureFlags({ ldContext })
}

export function usePatchAccountMutation() {
  const { queries } = useAccountQueries()
  return useMutation(queries.patchAccount)
}

export function useUsers() {
  const { queries } = useAccountQueries()
  const queryResult = useQuery(queries.getUsers)
  return queryResult
}

export function useDeleteUser({
  userId,
  resourcesReceiverUserId
}: {
  userId: string
  resourcesReceiverUserId?: string
}) {
  const deleteUserQuery = useDeleteUserMutation({ userId, resourcesReceiverUserId })
  const mutationResult = useMutation(deleteUserQuery)
  return mutationResult
}

export function useInvitationsInProgress() {
  const { queries } = useAccountQueries()
  const queryResult = useQuery({ ...queries.getInvitationsInProgress })
  return { ...queryResult }
}

export function useUpdateUserRoleMutation() {
  const { queries } = useAccountQueries()

  return useMutation(queries.updateUserRole)
}

export function useSendInvitation() {
  const { queries } = useAccountQueries()
  const mutationResult = useMutation(queries.sendInvitation)
  return mutationResult
}

export function useResendInvitation() {
  const { queries } = useAccountQueries()
  const mutationResult = useMutation(queries.resendInvitation)
  return mutationResult
}

export function useWithdrawInvitation() {
  const { queries } = useAccountQueries()
  const mutationResult = useMutation(queries.withdrawInvitation)
  return mutationResult
}

export function useApproveRequest() {
  const { queries } = useAccountQueries()
  const mutationResult = useMutation(queries.approveRequest)
  return mutationResult
}

export function useDeclineRequest() {
  const { queries } = useAccountQueries()
  const mutationResult = useMutation(queries.declineRequest)
  return mutationResult
}

export function useAccountBasemapsSettings() {
  const { queries } = useAccountQueries()
  const queryResult = useQuery(queries.getBasemapsSettings)
  return queryResult
}

export function useCreateBasemap() {
  const { queries } = useAccountQueries()
  const createBasemap = useMutation(queries.createBasemap)
  return createBasemap
}

export function useUpdateAccountBasemapsSettings() {
  const { queries } = useAccountQueries()
  const updateBasemapsSettings = useMutation(queries.updateBasemapsSettings)
  return updateBasemapsSettings
}

export function useAccountPalettesSettings() {
  const { queries } = useAccountQueries()
  return useQuery(queries.getPalettesSettings)
}

export function useUpdateAccountPalettesSettings() {
  const { queries } = useAccountQueries()
  const updatePalettesSettings = useMutation(queries.updatePalettesSettings)
  return updatePalettesSettings
}

export function useAccountCustomBasemapsList() {
  const { queries } = useAccountQueries()
  const queryResult = useQuery(queries.getCustomBasemapsList)
  return queryResult
}

export function useDeleteCustomBasemap() {
  const { queries } = useAccountQueries()
  const deleteBasemap = useMutation(queries.deleteBasemap)
  return deleteBasemap
}

export function useAccountWorkflowSettings() {
  const { queries } = useAccountQueries()
  return useQuery(queries.getWorkflowsSettings)
}

export function useUpdateAccountWorkflowSettings() {
  const { queries } = useAccountQueries()
  return useMutation(queries.updateWorkflowsSettings)
}

export function useBuilderGenAISettings() {
  const { queries } = useAccountQueries()
  return useQuery(queries.getBuilderGenAISettings)
}

export function useUpdateAccountBuilderGenAISettings() {
  const { queries } = useAccountQueries()
  return useMutation(queries.updateBuilderGenAISettings)
}

export function useGetSupportAccessStatus(accountId) {
  const { queries } = useAccountQueries()
  return useQuery(queries.getSupportAccessStatus(accountId))
}

export function useEnableSupportAccess() {
  const { queries } = useAccountQueries()
  const supportAccess = useMutation(queries.enableSupportAccess)
  return supportAccess
}

export function useRevokeSupportAccess() {
  const { queries } = useAccountQueries()
  const supportAccess = useMutation(queries.revokeSupportAccess)
  return supportAccess
}

export function useSubscritpionsList() {
  const { queries } = useAccountQueries()
  return useQuery(queries.getSubscriptionsList)
}

export function useDeleteSubscription() {
  const { queries } = useAccountQueries()
  return useMutation(queries.deleteSubscription)
}

export function useDODatasets(subscriptions: string[]) {
  const { queries } = useAccountQueries()
  return useQuery(queries.getDODatasets(subscriptions))
}
